import { isEmpty, isNil } from 'lodash';
import { useMemo } from 'react';
import useRecordContext from '../../../utilities/RecordContextProviderUtilities';
import {
    HYPERVISOR_TYPE,
    VCENTER_TYPE,
    VIRTUALMACHINE_TYPE,
} from '../../filters/virtual-infrastructure-type-filter/SubTypeHelper';
import { MONITORED_LIST_DELETE_URL } from './InfrastructureConstants';

/**
 * Custom hook to get virtual infrastructure context
 * Provides subType, id, and helper flags for infrastructure type checking
 * @returns {Object} Virtual infrastructure context
 * @returns {string} returns.subType - Infrastructure subtype
 * @returns {string} returns.id - Infrastructure ID
 * @returns {boolean} returns.hasSubType - Whether subType exists
 * @returns {boolean} returns.hasId - Whether ID exists
 * @returns {boolean} returns.isCreateAction - Whether this is a create action (no subType)
 * @returns {boolean} returns.isVirtualMachine - Whether this is a virtual machine
 * @returns {boolean} returns.isHypervisor - Whether this is a hypervisor
 * @returns {boolean} returns.isVCenter - Whether this is a vCenter
 */
export const useVirtualInfraContext = () => {
    const { type: subType, id } = useRecordContext();

    const context = useMemo(() => {
        const hasSubType = !isEmpty(subType);
        const hasId = !isEmpty(id);
        const isCreateAction = !hasSubType;

        return {
            subType,
            id,
            hasSubType,
            hasId,
            isCreateAction,
            isVirtualMachine: subType === VIRTUALMACHINE_TYPE,
            isHypervisor: subType === HYPERVISOR_TYPE,
            isVCenter: subType === VCENTER_TYPE,
        };
    }, [subType, id]);

    return context;
};

/**
 * Renders component based on subType match
 * @param {Object} config - Configuration object
 * @param {string} config.subType - Current subType
 * @param {string} config.targetType - Target type to match
 * @param {JSX.Element} config.component - Component to render if types match
 * @param {JSX.Element} config.fallback - Fallback to render if no match (default: null)
 * @returns {JSX.Element|null} Rendered component or fallback
 */
export const renderBySubType = ({ subType, targetType, component, fallback = null }) => {
    if (isNil(subType) || isNil(targetType)) {
        return fallback;
    }
    return subType === targetType ? component : fallback;
};

/**
 * Validates if virtual infrastructure context is ready for rendering
 * @param {Object} context - Virtual infrastructure context
 * @param {boolean} requireId - Whether ID is required (default: false)
 * @returns {boolean} True if context is valid
 */
export const isValidContext = (context, requireId = false) => {
    if (isNil(context)) {
        return false;
    }

    const { hasSubType, hasId } = context;

    if (requireId) {
        return hasSubType && hasId;
    }

    return hasSubType;
};

/**
 * Check if the given URL requires a specialized delete error handler
 * Used to determine if delete operations need custom error handling logic
 *
 * @param {string} url - API endpoint URL to check
 * @returns {boolean} True if URL is the device infrastructure delete endpoint
 *
 * @example
 * const needsHandler = shouldUseDeleteErrorHandler('/api/delete');
 */
export const shouldUseDeleteErrorHandler = (url) => url === MONITORED_LIST_DELETE_URL;

/**
 * Resolve the appropriate error handler based on the action URL
 * Centralizes error handler selection logic for infrastructure operations
 *
 * @param {string} url - API endpoint URL to check
 * @param {Function} handleDeleteError - Delete-specific error handler function
 * @returns {Function|null} Error handler function if URL matches, null otherwise
 *
 * @example
 * const errorHandler = resolveErrorHandler(actionUrl, handleDeleteError);
 * await apiCall(url, data, errorHandler);
 */
export const resolveErrorHandler = (url, handleDeleteError) => {
    return shouldUseDeleteErrorHandler(url) ? handleDeleteError : null;
};
